// src/components/risk/constants.js
// Centralized risk category definitions for the Risk Management system

// Risk category definitions matching the backend engine
export const RISK_CATEGORIES = [
  {
    min: 0, max: 19,
    name: 'Stable',
    key: 'stable',
    color: '#166534',
    bgColor: 'bg-green-900/30',
    borderColor: 'border-green-500/30',
    textColor: 'text-green-400',
    badge: 'Loyal',
    action: 'Quarterly touch',
    urgency: 0
  },
  {
    min: 20, max: 34,
    name: 'Low',
    key: 'low',
    color: '#22c55e',
    bgColor: 'bg-green-700/20',
    borderColor: 'border-green-500/30',
    textColor: 'text-green-500',
    badge: 'Good',
    action: 'Monitor',
    urgency: 1
  },
  {
    min: 35, max: 49,
    name: 'Moderate',
    key: 'moderate',
    color: '#eab308',
    bgColor: 'bg-yellow-900/20',
    borderColor: 'border-yellow-500/30',
    textColor: 'text-yellow-400',
    badge: 'Watch',
    action: 'Outreach within 14 days',
    urgency: 2
  },
  {
    min: 50, max: 64,
    name: 'Elevated',
    key: 'elevated',
    color: '#f97316',
    bgColor: 'bg-orange-900/20',
    borderColor: 'border-orange-500/30',
    textColor: 'text-orange-400',
    badge: 'Attention',
    action: 'Outreach within 7 days',
    urgency: 3
  },
  {
    min: 65, max: 79,
    name: 'High',
    key: 'high',
    color: '#ef4444',
    bgColor: 'bg-red-900/20',
    borderColor: 'border-red-500/30',
    textColor: 'text-red-400',
    badge: 'Priority',
    action: 'Outreach within 48 hours',
    urgency: 4
  },
  {
    min: 80, max: 89,
    name: 'Critical',
    key: 'critical',
    color: '#dc2626',
    bgColor: 'bg-red-800/30',
    borderColor: 'border-red-500/50',
    textColor: 'text-red-500',
    badge: 'Urgent',
    action: 'Same-day outreach',
    urgency: 5
  },
  {
    min: 90, max: 100,
    name: 'Severe',
    key: 'severe',
    color: '#7f1d1d',
    bgColor: 'bg-red-950/40',
    borderColor: 'border-red-600/50',
    textColor: 'text-red-600',
    badge: 'Emergency',
    action: 'Call immediately',
    urgency: 6
  }
];

// Category weight definitions from the scoring engine
export const CATEGORY_WEIGHTS = {
  engagement: {
    weight: 0.50,
    label: 'Engagement',
    color: '#3b82f6', // blue-500
    bgColor: 'bg-blue-500/20',
    description: 'Contact frequency, response rates, and communication history'
  },
  utilization: {
    weight: 0.18,
    label: 'Utilization',
    color: '#a855f7', // purple-500
    bgColor: 'bg-purple-500/20',
    description: 'Healthcare usage patterns and cost trends'
  },
  benefit_fit: {
    weight: 0.15,
    label: 'Benefit Fit',
    color: '#ec4899', // pink-500
    bgColor: 'bg-pink-500/20',
    description: 'Plan coverage alignment with client needs'
  },
  life_events: {
    weight: 0.07,
    label: 'Life Events',
    color: '#f59e0b', // amber-500
    bgColor: 'bg-amber-500/20',
    description: 'Major life changes that may trigger SEP eligibility'
  },
  external: {
    weight: 0.10,
    label: 'External',
    color: '#06b6d4', // cyan-500
    bgColor: 'bg-cyan-500/20',
    description: 'Market competition and carrier stability factors'
  }
};

// Get risk category for a given score
export function getRiskCategory(score) {
  const s = Number(score) || 0;
  return RISK_CATEGORIES.find(cat => s >= cat.min && s <= cat.max) || RISK_CATEGORIES[0];
}

// Get color for a score
export function getScoreColor(score) {
  return getRiskCategory(score).color;
}

// Intervention outcome options - values must match call_outcome_options.outcome_code in database
// NOTE: scoreImpact is for DISPLAY ONLY - actual scoring is handled by Lambda
// "Client Satisfied" resets engagement to 0 (neutral) - shown as "Resets" in UI
export const INTERVENTION_OUTCOMES = [
  {
    value: 'contact_satisfied',
    label: 'Client Satisfied',
    description: 'Resets contact score to neutral for 30 days',
    scoreImpact: 'reset', // Special value - engagement becomes 0
    color: 'green'
  },
  {
    value: 'contact_followup',
    label: 'Follow-Up Needed',
    description: 'Schedules callback, partial credit',
    scoreImpact: -5,
    color: 'yellow'
  },
  {
    value: 'no_answer_voicemail',
    label: 'No Answer',
    description: 'Left voicemail, minor penalty',
    scoreImpact: +5,
    color: 'gray'
  },
  {
    value: 'bad_phone_number',
    label: 'Bad Contact Info',
    description: 'Unable to reach - needs updated info',
    scoreImpact: +15,
    color: 'orange'
  },
  {
    value: 'escalate_manager',
    label: 'Escalate to Manager',
    description: 'Needs supervisor attention',
    scoreImpact: 0,
    color: 'red'
  }
];

// Alert type styling
export const ALERT_TYPES = {
  emergency: {
    bg: 'bg-red-100 dark:bg-red-900/40',
    text: 'text-red-800 dark:text-red-200',
    border: 'border-l-4 border-red-600',
    badge: 'bg-red-600 text-white',
    label: 'EMERGENCY'
  },
  urgent: {
    bg: 'bg-orange-100 dark:bg-orange-900/30',
    text: 'text-orange-800 dark:text-orange-200',
    border: 'border-l-4 border-orange-500',
    badge: 'bg-orange-500 text-white',
    label: 'URGENT'
  },
  warning: {
    bg: 'bg-yellow-100 dark:bg-yellow-900/30',
    text: 'text-yellow-800 dark:text-yellow-200',
    border: 'border-l-4 border-yellow-500',
    badge: 'bg-yellow-500 text-white',
    label: 'WARNING'
  }
};
